import { FC, Fragment, useMemo } from 'react';

import dayjs from 'dayjs';
import { isNonNullish } from 'remeda';
import useSWR from 'swr';

import Chip from '@mui/material/Chip';

import { AddTournamentGroupButton } from './addTournamentGroupButton/AddTournamentGroupButton';
import { TournamentGroupsTableActionsColumn } from './tournamentGroupsTableColumns/TournamentGroupsTableActionsColumn';

import css from './TournamentGroupsPage.module.css';

import { tournamentGroupService } from 'v2/api/tournamentGroupService';
import { tournamentsService } from 'v2/api/tournamentsService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { Loader } from 'v2/components/loader/Loader';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { toDetailedGroups } from 'v2/pages/tournamentGroups/utils';
import { TableColumn } from 'v2/types';
import { DetailedTournamentGroupModel } from 'v2/types/TournamentGroupModel';

export const TournamentGroupsPage: FC = () => {
  const {
    data: groupsData,
    isLoading: isGroupsLoading,
    mutate,
  } = useSWR('getTournamentGroups', tournamentGroupService.getTournamentGroups);

  const { data: tournamentsData, isLoading: isTournamentsLoading } = useSWR(
    'getAllTournaments',
    tournamentsService.getAllTournaments
  );

  const data = useMemo(() => toDetailedGroups(groupsData, tournamentsData), [groupsData, tournamentsData]);

  const columns: TableColumn<DetailedTournamentGroupModel>[] = useMemo(
    () => [
      { key: 'name', label: 'Name', style: { width: '10%' }, render: (row) => <Chip label={row.name} /> },
      {
        key: 'tournaments',
        label: 'Tournaments',
        style: { width: '20%' },
        render: (row) => (
          <Fragment>
            {row.tournaments.map((tournament) => (
              <Chip variant="outlined" label={tournament} />
            ))}
          </Fragment>
        ),
      },
      {
        key: 'startDate',
        label: 'Start Date',
        render: (row) => (row.startDate ? dayjs(row.startDate).format('DD.MM.YYYY HH:mm') : '-'),
      },
      {
        key: 'endDate',
        label: 'End Date',
        render: (row) => (row.endDate ? dayjs(row.endDate).format('DD.MM.YYYY HH:mm') : '-'),
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <TournamentGroupsTableActionsColumn tournamentGroup={row} onRevalidate={mutate} />,
      },
    ],
    [mutate]
  );

  return (
    <PageLayout className={css.tournamentGroupsPage} overflow="hidden">
      <PageTitle action={isNonNullish(groupsData) && <AddTournamentGroupButton onSuccess={mutate} />}>
        Tournament Groups Settings
      </PageTitle>

      {isGroupsLoading || isTournamentsLoading ? <Loader /> : <DataTable columns={columns} data={data} />}
    </PageLayout>
  );
};
